import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Zap, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('register.passwordsNoMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('register.passwordMinLength'));
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/onboarding');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030508] flex">
      {/* Left side - Visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.pexels.com/photos/7567307/pexels-photo-7567307.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          }}
        />
        <div className="absolute inset-0 bg-[#030508]/80" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-8">
              <Shield className="w-12 h-12 text-emerald-500" />
            </div>
            <h2 className="text-3xl font-black text-white mb-4" style={{ fontFamily: 'Chivo' }}>
              {t('register.agentsSidebar')}
            </h2>
            <p className="text-slate-400 font-mono text-sm max-w-sm">
              {t('register.agentsDesc')}
            </p>
          </div>
        </div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-white flex items-center justify-center">
                <Zap className="w-6 h-6 text-[#030508]" />
              </div>
              <span className="text-2xl font-black tracking-tight text-white" style={{ fontFamily: 'Chivo' }}>
                BlackOre<span className="text-emerald-500">X</span>
              </span>
            </div>
            <p className="text-slate-500 text-sm font-mono">{t('register.badge')}</p>
          </div>

          <h1 className="text-3xl font-black text-white mb-2 tracking-tight" style={{ fontFamily: 'Chivo' }}>
            {t('register.title')}
          </h1>
          <p className="text-slate-400 mb-8">{t('register.subtitle')}</p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 mb-6 text-sm font-mono" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 font-semibold">
                {t('register.operatorName')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#090B0F] border border-white/10 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-white/30 transition-colors"
                placeholder="John Doe"
                required
                data-testid="register-name-input"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 font-semibold">
                {t('common.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#090B0F] border border-white/10 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-white/30 transition-colors"
                placeholder="operator@blackore.com"
                required
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 font-semibold">
                {t('common.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#090B0F] border border-white/10 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-white/30 transition-colors pr-12"
                  placeholder="••••••••••"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 font-semibold">
                {t('register.confirmPassword')}
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-[#090B0F] border border-white/10 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-white/30 transition-colors"
                placeholder="••••••••••"
                required
                data-testid="register-confirm-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-[#030508] py-3 font-bold flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors disabled:opacity-50 mt-8"
              data-testid="register-submit-button"
            >
              {loading ? (
                <span className="font-mono">{t('register.creating')}</span>
              ) : (
                <>
                  <span>{t('register.requestAccess')}</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-slate-500 mt-8 text-sm">
            {t('register.alreadyAccess')}{' '}
            <Link to="/login" className="text-white hover:text-emerald-400 transition-colors font-medium" data-testid="login-link">
              {t('register.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
